import { createFileRoute, Link } from "@tanstack/react-router";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Plane,
  Phone,
  Clock,
  Shield,
  UserCheck,
  MapPin,
  Star,
  CheckCircle2,
  Briefcase,
  Award,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CorporateAccountForm from "@/components/CorporateAccountForm";
import FloatingButtons from "@/components/FloatingButtons";
import CampaignHeroCta from "@/components/CampaignHeroCta";
import ContactButtons from "@/components/ContactButtons";
import TextMariaSection from "@/components/TextMariaSection";
import FamilyChauffeurs from "@/components/FamilyChauffeurs";
import BrandLine from "@/components/BrandLine";
import heroImage from "@/assets/sprinter-exec-yellow.jpg?format=webp&quality=72&w=1400&imagetools";
import fboImage from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1400&imagetools";
import nightImage from "@/assets/mercedes-benz-stadium-sprinter-limo-party-bus.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/private-jet-car-service-atlanta";
const PAGE_TITLE = "Private Jet Car Service Atlanta | FBO Chauffeur Transportation";
const PAGE_DESC =
  "Premier private jet car service in Atlanta. Direct FBO transportation, planeside Mercedes-Benz Sprinter pickup, and professional chauffeurs for private aviation clients across PDK, Fulton County & ATL FBOs.";

export const Route = createFileRoute("/private-jet-car-service-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "private jet car service Atlanta, FBO transportation Atlanta, chauffeur service private aviation Atlanta, PDK airport car service, Atlanta FBO chauffeur, private jet ground transportation Atlanta, Signature Flight Support Atlanta car service, Mercedes Sprinter private jet Atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": PAGE_URL,
          name: "Atlanta Luxury Sprinters, Private Jet Car Service Atlanta",
          image: heroImage,
          url: PAGE_URL,
          telephone: "+1-678-431-7222",
          priceRange: "$$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "3401 Norman Berry Dr Suite 146",
            addressLocality: "Atlanta",
            addressRegion: "GA",
            postalCode: "30344",
            addressCountry: "US",
          },
          areaServed: [
            { "@type": "City", name: "Atlanta" },
            { "@type": "Airport", name: "Hartsfield-Jackson Atlanta International Airport (ATL)" },
            { "@type": "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
            { "@type": "Airport", name: "Fulton County Airport (FTY)" },
          ],
          serviceType: "Private Jet Car Service & FBO Chauffeur Transportation",
          description: PAGE_DESC,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Do you offer planeside pickup at Atlanta FBOs?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Our chauffeurs provide direct tarmac access at Signature Flight Support, Atlantic Aviation, Million Air, and other private aviation terminals at PDK, FTY, and ATL.",
              },
            },
            {
              "@type": "Question",
              name: "Which Atlanta airports do you serve for private jet clients?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We serve all major Atlanta-area private aviation facilities including DeKalb-Peachtree (PDK), Fulton County (FTY), Hartsfield-Jackson (ATL) FBOs, and Cobb County International Airport (RYY).",
              },
            },
            {
              "@type": "Question",
              name: "What vehicles do you use for private jet car service?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Our fleet features Mercedes-Benz Sprinter Limousines in Jet, Executive, and Limo configurations, plus Suburban SUVs, all chauffeur-driven for VIP private aviation clients.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: PrivateJetPage,
});

function AnimatedSection({
  children,
  className = "",
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

const fboList = [
  "Signature Flight Support, ATL",
  "Atlantic Aviation, PDK",
  "Million Air, PDK",
  "Signature Flight Support, PDK",
  "Epps Aviation, PDK",
  "Fulton Jet Center, FTY",
  "Hill Aircraft, FTY",
  "Atlantic Aviation, RYY",
];

const benefits = [
  {
    icon: Plane,
    title: "Planeside FBO Pickup",
    desc: "Direct tarmac access at every major Atlanta FBO. Your Mercedes-Benz Sprinter is waiting the moment your jet shuts down.",
  },
  {
    icon: Clock,
    title: "Live Tail Number Tracking",
    desc: "We monitor your aircraft tail number in real time and adjust for early arrivals or last-minute schedule changes.",
  },
  {
    icon: UserCheck,
    title: "Discreet Professional Chauffeurs",
    desc: "Background-checked, formally attired chauffeurs trained in private aviation protocol and VIP confidentiality.",
  },
  {
    icon: Shield,
    title: "Fully Insured & Licensed",
    desc: "TCP-licensed, fully insured operator with a flawless safety record across thousands of private aviation transfers.",
  },
  {
    icon: Briefcase,
    title: "Corporate & Executive Travel",
    desc: "Dedicated account management for flight departments, executive assistants, and concierge teams.",
  },
  {
    icon: Award,
    title: "5-Star VIP Experience",
    desc: "Bottled water, climate control, Wi-Fi, and a quiet cabin, every detail handled before you board.",
  },
];

const serviceAreas = [
  "DeKalb-Peachtree Airport (PDK)",
  "Hartsfield-Jackson FBOs (ATL)",
  "Fulton County Airport / Brown Field (FTY)",
  "Cobb County International (RYY)",
  "Buckhead & Midtown",
  "Downtown Atlanta",
  "Sandy Springs & Dunwoody",
  "Alpharetta & Johns Creek",
];

const steps = [
  {
    n: "01",
    title: "Share Your Itinerary",
    desc: "Text me your tail number, FBO, arrival time and passenger count.",
  },
  {
    n: "02",
    title: "We Track Your Flight",
    desc: "Live monitoring ensures the chauffeur is positioned planeside before your jet arrives.",
  },
  {
    n: "03",
    title: "Step Off, Step In",
    desc: "Walk from your aircraft directly into a chilled, ready Mercedes-Benz Sprinter.",
  },
  {
    n: "04",
    title: "Arrive in Style",
    desc: "Enjoy a quiet, climate-controlled ride to any destination across metro Atlanta.",
  },
];

function PrivateJetPage() {
  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-24 pb-12">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Executive Mercedes-Benz Sprinter interior ready for a private jet chauffeur transfer in Atlanta"
              width={1920}
              height={1080}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/30" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="max-w-4xl mx-auto"
            >
              <p className="text-purple-light text-xs font-medium tracking-[0.4em] uppercase mb-6">
                Atlanta Private Aviation • FBO Chauffeur Service
              </p>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold leading-[1.05] mb-8 text-white">
                <span className="hero-gradient-text">Private Jet Car Service</span>{" "}
                in Atlanta
              </h1>

              <p className="text-white/80 text-lg sm:text-xl leading-relaxed mb-10 max-w-3xl mx-auto">
                You're landing with somewhere to be. My chauffeurs coordinate your Suburban SUV or
                Mercedes-Benz Sprinter pickup at PDK, ATL FBOs, Fulton County and every major
                Atlanta-area FBO.
              </p>

              <CampaignHeroCta>
                <ContactButtons variant="hero" />
              </CampaignHeroCta>
              <BrandLine />
            </motion.div>
          </div>
        </section>

        {/* Intro */}
        <AnimatedSection className="section-padding bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="accent-line-center mb-6" />
            <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
              Private Aviation Specialists
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-8">
              Atlanta's Premier Chauffeur Service for Private Aviation
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              Atlanta Luxury Sprinters is the trusted ground transportation partner for private jet
              passengers, flight departments, and aviation concierge teams across Atlanta. From the
              moment your aircraft touches down at any Atlanta-area FBO, our Mercedes-Benz Sprinter
              chauffeurs deliver a seamless, planeside-to-destination experience.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Whether you're flying into <strong className="text-foreground">DeKalb-Peachtree (PDK)</strong>,
              a <strong className="text-foreground">Hartsfield-Jackson FBO</strong>, or
              <strong className="text-foreground"> Fulton County Airport</strong>, our team
              coordinates directly with your flight crew to ensure flawless arrivals and departures.
            </p>
          </div>
        </AnimatedSection>

        {/* Benefits Grid */}
        <AnimatedSection className="section-padding bg-surface">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                Why Choose Atlanta Luxury Sprinters
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground">
                Built for Private Aviation Clients
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="luxury-card rounded-lg p-8 hover:shadow-xl transition-shadow"
                >
                  <div className="w-14 h-14 rounded-sm bg-accent flex items-center justify-center mb-6">
                    <b.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                    {b.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* FBOs Served */}
        <AnimatedSection className="section-padding bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <img
                  src={fboImage}
                  alt="Jet Style Mercedes-Benz Sprinter with white leather captain seats for Atlanta FBO transfers"
                  width={1280}
                  height={832}
                  loading="lazy"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
              <div>
                <div className="accent-line-left mb-6" />
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                  FBO Transportation Atlanta
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                  Every Major Atlanta FBO, Covered
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                  Our chauffeurs operate daily at Atlanta's busiest private aviation terminals.
                  We know each FBO's tarmac access procedures, security protocols, and concierge teams.
                </p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {fboList.map((fbo) => (
                    <li key={fbo} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{fbo}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* How It Works */}
        <AnimatedSection className="section-padding bg-surface">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                The Process
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground">
                Effortless From Wheels-Down to Destination
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((s) => (
                <div key={s.n} className="text-center">
                  <div className="text-5xl font-heading text-primary/30 mb-4">{s.n}</div>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                    {s.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Service Areas */}
        <AnimatedSection className="section-padding bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="accent-line-left mb-6" />
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                  Coverage Area
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                  Serving All of Metro Atlanta
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                  From private terminals to luxury hotels, corporate boardrooms, and residential
                  estates, we provide private jet ground transportation throughout the
                  Atlanta metropolitan area.
                </p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {serviceAreas.map((area) => (
                    <li key={area} className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <img
                  src={nightImage}
                  alt="Mercedes-Benz Sprinter Limousine lounge with starlight ceiling for evening private jet arrivals"
                  width={1280}
                  height={832}
                  loading="lazy"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Testimonial / Trust */}
        <AnimatedSection className="section-padding bg-surface">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-primary text-primary" />
              ))}
            </div>
            <blockquote className="font-heading text-2xl sm:text-3xl text-foreground leading-relaxed mb-8 italic">
              "Atlanta Luxury Sprinters has become our exclusive ground transportation partner for every
              client arriving at PDK. Flawless execution, every single flight."
            </blockquote>
            <p className="text-muted-foreground tracking-widest uppercase text-sm">
             , Director of Operations, Atlanta Private Aviation Concierge
            </p>
          </div>
        </AnimatedSection>

        {/* Internal Links */}
        <AnimatedSection className="section-padding bg-background">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                Explore More
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground">
                Related Services
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/corporate-transportation-atlanta"
                className="luxury-card rounded-lg p-6 hover:shadow-xl transition-all group"
              >
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  Corporate Transportation Atlanta
                </h3>
                <p className="text-muted-foreground text-sm">
                  Dedicated corporate accounts for executive teams and assistants.
                </p>
              </Link>
              <Link
                to="/"
                className="luxury-card rounded-lg p-6 hover:shadow-xl transition-all group"
              >
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  Atlanta Chauffeur Service
                </h3>
                <p className="text-muted-foreground text-sm">
                  Explore our full Mercedes-Benz Sprinter fleet and chauffeur services.
                </p>
              </Link>
              <Link
                to="/rentals"
                className="luxury-card rounded-lg p-6 hover:shadow-xl transition-all group"
              >
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  Sprinter & Party Bus Rentals
                </h3>
                <p className="text-muted-foreground text-sm">
                  Group transportation for events, corporate, and nightlife.
                </p>
              </Link>
              <a
                href="/#airport"
                className="luxury-card rounded-lg p-6 hover:shadow-xl transition-all group block"
              >
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  Hartsfield-Jackson Airport Service
                </h3>
                <p className="text-muted-foreground text-sm">
                  Commercial airport transfers from ATL terminals.
                </p>
              </a>
            </div>
          </div>
        </AnimatedSection>

        {/* Final CTA */}
        <TextMariaSection
          title="Text Me Your Flight Plans"
          body="Send me your tail number, FBO, destination, passengers and bags. I'll check availability personally and send your quote in writing."
        />
        <FamilyChauffeurs compact />
      </main>
      <CorporateAccountForm compact />
      <Footer />
      <FloatingButtons />
    </>
  );
}
